import { FC } from 'react'

import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl'
import { shallowEqual, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'

import AnalyticsDetailCard from '@components/Analytics/DetailCard/DetailCard'
import { isNumber } from '@components/Analytics/PrePurchase/PrePurchase'
import { Hr } from '@components/UI/Hr/Hr.styled'
import { AnalyticsCustomPrice } from '@components/UI/Price/Price'
import { CPM_EXPLAINED, TRIBE_PRICING_PAGE_POST_PURCHASE_METRICS_UTM } from '@constants'
import { IApplicationState } from '@store'
import { selectSubmissionPerformance } from '@store/performance'
import { selectSubmissionId } from '@store/router'
import { selectSubmission, selectSubTotalCurrency } from '@store/submissions'
import Theme from '@theme'
import { Link } from '@tribegroup'
import { abbreviateNumberOrDash } from '@utils'
import { AnalyticsPostPerformanceGrid } from '../PostPerformance.styled'

const externalLink = TRIBE_PRICING_PAGE_POST_PURCHASE_METRICS_UTM

const AnalyticsPostPerformancePost: FC<WrappedComponentProps> = ({ intl }) => {
  const match = useRouteMatch()

  const { performance, followers, currency } = useSelector((state: IApplicationState) => {
    const submissionId = selectSubmissionId(match) as number

    const { identity_followers_count } = selectSubmission(state, submissionId)

    return {
      followers: identity_followers_count,
      currency: selectSubTotalCurrency(state, submissionId),
      performance: selectSubmissionPerformance(state, submissionId),
    }
  }, shallowEqual)

  const cpm = performance.cost_per_mille
  const cpe = performance.cost_per_engagement
  const reachPct = performance.reach_pct
  const engagementPct = performance.engagement_rate

  return (
    <>
      <AnalyticsPostPerformanceGrid>
        <AnalyticsDetailCard
          title="submission.postPerformance.post.engagement"
          content={abbreviateNumberOrDash(performance?.engagement)}
          elementName="post-performance-engagement"
          productMetricGroup="post_purchase"
          productMetricValue="engagement"
          externalLink={externalLink}
          featured
        />
        {cpe && (
          <AnalyticsDetailCard
            title="submission.postPerformance.post.CPE"
            values={{
              taxDisplay: intl.formatMessage({
                id: `core.currency.taxDisplay.${currency ?? 'generic'}`,
              }),
            }}
            content={isNumber(cpe) && <AnalyticsCustomPrice currency={currency} quantity={cpe} />}
            elementName="post-performance-cpe"
            productMetricGroup="post_purchase"
            productMetricValue="cpe"
            externalLink={externalLink}
            featured
          />
        )}
        <AnalyticsDetailCard
          title="submission.postPerformance.post.impressions"
          content={abbreviateNumberOrDash(performance?.impressions)}
          elementName="post-performance-impressions"
          productMetricGroup="post_purchase"
          productMetricValue="impressions"
          externalLink={externalLink}
          featured
        />
      </AnalyticsPostPerformanceGrid>
      <Hr backgroundColor={Theme.grey400Color} />
      <AnalyticsPostPerformanceGrid topOuterSpacing={1.75}>
        <AnalyticsDetailCard
          title="submission.postPerformance.followers"
          content={abbreviateNumberOrDash(followers)}
          elementName="post-performance-followers"
          productMetricGroup="post_purchase"
          productMetricValue="followers"
          externalLink={externalLink}
        />
        <AnalyticsDetailCard
          title="submission.postPerformance.post.engagementPercentage"
          content={
            isNumber(engagementPct) && (
              <FormattedMessage id="core.text.percent" values={{ value: engagementPct }} />
            )
          }
          elementName="post-performance-engagement-percentage"
          productMetricGroup="post_purchase"
          productMetricValue="engagement_pct"
          externalLink={externalLink}
        />
        <AnalyticsDetailCard
          title="submission.postPerformance.post.reach"
          content={abbreviateNumberOrDash(performance?.reach)}
          elementName="post-performance-reach"
          productMetricGroup="post_purchase"
          productMetricValue="reach"
          externalLink={externalLink}
        />
        <AnalyticsDetailCard
          title="submission.postPerformance.post.reachPercentage"
          content={
            isNumber(reachPct) && (
              <FormattedMessage id="core.text.percent" values={{ value: reachPct }} />
            )
          }
          elementName="post-performance-reach-percentage"
          productMetricGroup="post_purchase"
          productMetricValue="reach_pct"
          externalLink={externalLink}
        />

        {cpm && (
          <AnalyticsDetailCard
            title="submission.postPerformance.post.CPM"
            content={isNumber(cpm) && <AnalyticsCustomPrice currency={currency} quantity={cpm} />}
            elementName="post-performance-cpm"
            productMetricGroup="post_purchase"
            productMetricValue="cpm"
            externalLink={externalLink}
            values={{
              link: (
                <Link href={CPM_EXPLAINED} target="_blank" color={Theme.primaryColor} lowercase>
                  <FormattedMessage id="core.text.clickHere" />
                </Link>
              ),
            }}
          />
        )}
      </AnalyticsPostPerformanceGrid>
    </>
  )
}

export { AnalyticsPostPerformancePost }
export default injectIntl(AnalyticsPostPerformancePost)
